import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { useData } from '../contexts/DataContext';
import { useTranslation } from 'react-i18next';
import { getSampleDataByTemplate, downloadCSV } from '../utils/sampleDataGenerator';

function DataUpload({ showToast }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dataSources, addDataSource, updateDataSource, deleteDataSource, sourceDefinitions } = useData();
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [uploadMode, setUploadMode] = useState('merge');
  const fileInputRef = useRef(null);

  const uploadedDataSources = dataSources.filter(ds => ds.type === 'CSV' || ds.type === 'Excel');

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return Promise.reject('No file');

    // Validate source selection
    if (!selectedSourceId) {
      return Promise.reject('No source selected');
    }

    const selectedSource = sourceDefinitions.find(s => s.id === selectedSourceId);
    if (!selectedSource) {
      return Promise.reject('Invalid source');
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return Promise.reject('File too large');
    }

    return new Promise((resolve, reject) => {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      let parsedData = null;
      let sheets = [];

      if (fileExtension === 'csv') {
        // Parse CSV
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            if (results.errors.length > 0) {
              console.error('CSV parsing errors:', results.errors);
            }

            parsedData = {
              sheets: [{
                name: file.name.replace('.csv', ''),
                data: results.data,
                headers: results.meta.fields || []
              }]
            };
            
            const newDataSource = {
              id: 'DS-' + Date.now(),
              name: file.name,
              sourceName: selectedSource.name,
              sourceCode: selectedSource.code,
              sourceId: selectedSource.id,
              type: 'CSV',
              fileSize: file.size,
              uploadDate: new Date().toISOString(),
              lastSync: new Date().toLocaleString('vi-VN'),
              data: parsedData,
              sheetCount: 1,
              rowCount: results.data.length,
              columnCount: results.meta.fields?.length || 0,
              status: 'active'
            };

            addDataSource(newDataSource);
            resolve();
          },
          error: (error) => {
            console.error('CSV parsing error:', error);
            reject(error);
          }
        });
      } else if (['xlsx', 'xls'].includes(fileExtension)) {
        // Parse Excel
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            
            sheets = workbook.SheetNames.map(sheetName => {
              const worksheet = workbook.Sheets[sheetName];
              const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
              
              if (jsonData.length === 0) return null;
              
              const headers = jsonData[0].map(h => String(h || '').trim()).filter(h => h);
              const rows = jsonData.slice(1)
                .filter(row => row.some(cell => cell !== undefined && cell !== ''))
                .map(row => {
                  const obj = {};
                  headers.forEach((header, index) => {
                    const value = row[index];
                    obj[header] = value !== undefined && value !== '' ? value : null;
                  });
                  return obj;
                });

              return {
                name: sheetName,
                data: rows,
                headers: headers
              };
            }).filter(sheet => sheet !== null && sheet.data.length > 0);

            if (sheets.length === 0) {
              showToast('File Excel không chứa dữ liệu hợp lệ', 'error');
              setUploading(false);
              return;
            }

            parsedData = { sheets };
            
            const totalRows = sheets.reduce((sum, sheet) => sum + sheet.data.length, 0);
            const maxColumns = Math.max(...sheets.map(sheet => sheet.headers.length));

            const newDataSource = {
              id: 'DS-' + Date.now(),
              name: file.name,
              sourceName: selectedSource.name,
              sourceCode: selectedSource.code,
              sourceId: selectedSource.id,
              type: 'Excel',
              fileSize: file.size,
              uploadDate: new Date().toISOString(),
              lastSync: new Date().toLocaleString('vi-VN'),
              data: parsedData,
              sheetCount: sheets.length,
              rowCount: totalRows,
              columnCount: maxColumns,
              status: 'active'
            };

            addDataSource(newDataSource);
            resolve();
          } catch (error) {
            console.error('Excel parsing error:', error);
            reject(error);
          }
        };
        reader.onerror = () => {
          reject(new Error('File read error'));
        };
        reader.readAsArrayBuffer(file);
      } else {
        reject(new Error('Unsupported file type'));
      }
    });
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files).filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return ['csv', 'xls', 'xlsx'].includes(ext);
    });
    
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      showToast(`Đã chọn ${files.length} file`, 'success');
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files).filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return ['csv', 'xls', 'xlsx'].includes(ext);
    });
    
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      showToast(`Đã chọn ${files.length} file`, 'success');
    }
    // Reset input
    e.target.value = '';
  };

  // Remove file from selected list
  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Upload all selected files
  const handleUploadAll = async () => {
    if (!selectedSourceId) {
      showToast('Vui lòng chọn nguồn dữ liệu', 'error');
      return;
    }

    if (selectedFiles.length === 0) {
      showToast('Vui lòng chọn file để upload', 'error');
      return;
    }

    const selectedSource = sourceDefinitions.find(s => s.id === selectedSourceId);
    if (!selectedSource) {
      showToast('Nguồn dữ liệu không hợp lệ', 'error');
      return;
    }

    setUploading(true);

    // Handle upload mode
    if (uploadMode === 'replace') {
      // Delete all existing files from this source
      const existingFiles = dataSources.filter(ds => 
        ds.sourceId === selectedSourceId || 
        ds.sourceName === selectedSource.name || 
        ds.sourceCode === selectedSource.code
      );
      existingFiles.forEach(file => deleteDataSource(file.id));
      showToast(`Đã xóa ${existingFiles.length} file cũ`, 'info');
    } else if (uploadMode === 'merge') {
      // Delete files with same name
      const newFileNames = selectedFiles.map(f => f.name);
      const filesToDelete = dataSources.filter(ds => 
        (ds.sourceId === selectedSourceId || ds.sourceName === selectedSource.name || ds.sourceCode === selectedSource.code) &&
        newFileNames.includes(ds.name)
      );
      filesToDelete.forEach(file => deleteDataSource(file.id));
      if (filesToDelete.length > 0) {
        showToast(`Đã đè ${filesToDelete.length} file trùng tên`, 'info');
      }
    }
    // uploadMode === 'append': do nothing, just add new files

    let successCount = 0;
    let failCount = 0;

    for (const file of selectedFiles) {
      try {
        await handleFileUpload(file);
        successCount++;
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        failCount++;
      }
    }

    setUploading(false);
    setSelectedFiles([]);

    if (successCount > 0) {
      showToast(`Upload thành công ${successCount} file${failCount > 0 ? `, thất bại ${failCount} file` : ''}`, 'success');
      // Redirect to Data Repository after 1 second
      setTimeout(() => {
        navigate('/data-repository');
      }, 1000);
    } else {
      showToast('Không có file nào được upload thành công', 'error');
    }
  };

  // View file
  const handleView = (dataSource) => {
    setSelectedFile(dataSource);
    setPreviewData(dataSource.data);
  };

  // Delete file
  const handleDelete = (id) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa file dữ liệu này?')) {
      deleteDataSource(id);
      showToast('Xóa file thành công', 'success');
    }
  };

  // Download file
  const handleDownload = (dataSource) => {
    try {
      if (dataSource.type === 'CSV') {
        const sheet = dataSource.data.sheets[0];
        const csv = Papa.unparse(sheet.data);
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = dataSource.name;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const workbook = XLSX.utils.book_new();
        dataSource.data.sheets.forEach(sheet => {
          const worksheet = XLSX.utils.json_to_sheet(sheet.data);
          XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name);
        });
        XLSX.writeFile(workbook, dataSource.name);
      }
      showToast('Tải file thành công', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showToast('Lỗi khi tải file: ' + error.message, 'error');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN');
  };

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="page-header-sticky">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-cloud-upload me-2"></i>
            {t('dataUpload.title')}
          </h2>
          <p className="text-muted mb-0">
            {t('dataUpload.subtitle')}
          </p>
        </div>
      </div>

      {/* Select Data Source - Professional Design */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="card-body p-4">
          <div className="d-flex align-items-center mb-3">
            <div className="p-3 bg-white bg-opacity-25 rounded-circle me-3">
              <i className="bi bi-folder2-open fs-4 text-white"></i>
            </div>
            <div>
              <h5 className="mb-1 text-white">{t('dataUpload.selectSource')}</h5>
              <p className="mb-0 text-white text-opacity-75 small">
                {t('dataUpload.selectSourceDesc')}
              </p>
            </div>
          </div>

          {!selectedSourceId && (
            <div className="alert alert-light border-0 mb-3">
              <div className="d-flex align-items-center">
                <i className="bi bi-info-circle text-primary me-2"></i>
                <small>{t('dataUpload.selectSourceDesc')}</small>
              </div>
            </div>
          )}

          <div className="row g-3">
            {sourceDefinitions
              .filter(s => s.status === 'active')
              .map(source => {
                const isSelected = selectedSourceId === source.id;
                const getSourceIcon = (code) => {
                  const icons = {
                    'CORE_BANKING': 'bi-bank2',
                    'CIC_DATA': 'bi-shield-check',
                    'CUSTOMER_EXCEL': 'bi-file-earmark-excel',
                    'CREDIT_DB': 'bi-credit-card-2-front',
                    'RISK_SYSTEM': 'bi-exclamation-triangle',
                    'TRANSACTION_LOG': 'bi-receipt'
                  };
                  return icons[code] || 'bi-database';
                };
                
                const getSourceColor = (code) => {
                  const colors = {
                    'CORE_BANKING': '#0d6efd',
                    'CIC_DATA': '#198754',
                    'CUSTOMER_EXCEL': '#20c997',
                    'CREDIT_DB': '#0dcaf0',
                    'RISK_SYSTEM': '#ffc107',
                    'TRANSACTION_LOG': '#6f42c1'
                  };
                  return colors[code] || '#6c757d';
                };

                return (
                  <div key={source.id} className="col-md-6 col-lg-4">
                    <div
                      className={`card h-100 border-0 shadow-sm position-relative ${isSelected ? 'border border-3 border-success' : ''}`}
                      style={{
                        cursor: 'pointer',
                        transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        backgroundColor: isSelected ? '#f8fff9' : 'white'
                      }}
                      onClick={() => setSelectedSourceId(source.id)}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.transform = 'translateY(-4px)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      {isSelected && (
                        <div className="position-absolute top-0 end-0 m-2">
                          <div className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '28px', height: '28px' }}>
                            <i className="bi bi-check-lg fw-bold"></i>
                          </div>
                        </div>
                      )}
                      
                      <div className="card-body p-3">
                        <div className="d-flex align-items-start mb-2">
                          <div 
                            className="p-2 rounded me-2 flex-shrink-0"
                            style={{ backgroundColor: getSourceColor(source.code) + '15' }}
                          >
                            <i className={`bi ${getSourceIcon(source.code)} fs-5`} style={{ color: getSourceColor(source.code) }}></i>
                          </div>
                          <div className="flex-grow-1 min-w-0">
                            <h6 className="mb-0 text-truncate" style={{ fontSize: '0.9rem' }}>
                              {source.name}
                            </h6>
                            <span 
                              className="badge mt-1" 
                              style={{ 
                                backgroundColor: getSourceColor(source.code) + '20',
                                color: getSourceColor(source.code),
                                fontSize: '0.7rem',
                                fontWeight: '600'
                              }}
                            >
                              {source.code}
                            </span>
                          </div>
                        </div>
                        <p className="mb-0 small text-muted" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                          {source.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {selectedSourceId && (
            <div className="mt-3 p-3 bg-white bg-opacity-25 rounded">
              <div className="d-flex align-items-center text-white">
                <i className="bi bi-check-circle-fill me-2"></i>
                <small>
                  <strong>Nguồn đã chọn:</strong> {sourceDefinitions.find(s => s.id === selectedSourceId)?.name}
                </small>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Templates for Selected Source */}
      {selectedSourceId && (() => {
        const selectedSource = sourceDefinitions.find(s => s.id === selectedSourceId);
        const hasTemplates = selectedSource?.templateFiles && selectedSource.templateFiles.length > 0;
        
        return hasTemplates ? (
          <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: '#fff8e1' }}>
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <i className="bi bi-download fs-5 text-warning me-2"></i>
                <div>
                  <h6 className="mb-0">Templates cho nguồn: <strong>{selectedSource.name}</strong></h6>
                  <small className="text-muted">Download template mẫu, điền dữ liệu và upload lên hệ thống</small>
                </div>
              </div>

              <div className="row g-3">
                {selectedSource.templateFiles.map((template, index) => {
                  const isExcel = template.type === 'excel';
                  return (
                    <div key={index} className={isExcel ? 'col-12' : 'col-md-6'}>
                      <div className={`card border ${isExcel ? 'border-success bg-light' : 'border-primary bg-white'} h-100`}>
                        <div className="card-body p-3">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="flex-grow-1">
                              <h6 className="mb-1">
                                <i className={`bi ${isExcel ? 'bi-file-earmark-excel text-success' : 'bi-file-earmark-spreadsheet text-primary'} me-2`}></i>
                                {template.name}
                              </h6>
                              <small className="text-muted d-block">{template.description}</small>
                              {template.size && (
                                <span className="badge bg-secondary mt-1" style={{ fontSize: '0.7rem' }}>
                                  {template.size}
                                </span>
                              )}
                            </div>
                            <div className="ms-3">
                              <button 
                                className={`btn ${isExcel ? 'btn-success' : 'btn-primary'} btn-sm`}
                                onClick={() => {
                                  if (!isExcel) {
                                    const sampleData = getSampleDataByTemplate(template.name);
                                    if (sampleData) {
                                      downloadCSV(template.name, sampleData);
                                      showToast(`Đã tải ${template.name} với dữ liệu mẫu`, 'success');
                                    } else {
                                      showToast('Template chưa có dữ liệu mẫu', 'warning');
                                    }
                                  } else {
                                    showToast('Excel template sẽ được triển khai trong phiên bản tới', 'info');
                                  }
                                }}
                              >
                                <i className="bi bi-download me-1"></i>
                                Download
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="alert alert-info mt-3 mb-0 d-flex align-items-start">
                <i className="bi bi-lightbulb me-2 mt-1"></i>
                <div>
                  <strong>Hướng dẫn sử dụng:</strong>
                  <ol className="mb-0 ps-3 mt-1" style={{ fontSize: '0.85rem' }}>
                    <li>Download template phù hợp</li>
                    <li>Mở file và điền dữ liệu (giữ nguyên tên columns/sheets)</li>
                    <li>Lưu file và upload bên dưới</li>
                    <li>Dữ liệu sẽ tự động gắn với nguồn <strong>{selectedSource.name}</strong></li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        ) : null;
      })()}

      {/* Upload Area */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div
            className="upload-area text-center p-5 border border-2 border-dashed rounded"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            style={{ 
              cursor: uploading ? 'not-allowed' : 'pointer', 
              backgroundColor: uploading ? '#f0f0f0' : '#f8f9fa',
              transition: 'all 0.3s ease'
            }}
          >
            {uploading ? (
              <div>
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Đang tải...</span>
                </div>
                <h5>Đang xử lý file...</h5>
                <p className="text-muted mb-0">Vui lòng đợi</p>
              </div>
            ) : (
              <>
                <i className="bi bi-cloud-upload fs-1 text-primary mb-3" style={{ fontSize: '4rem' }}></i>
                <h4 className="mb-2">Kéo thả file vào đây hoặc click để chọn</h4>
                <p className="text-muted mb-3">
                  <i className="bi bi-info-circle me-1"></i>
                  Hỗ trợ: <strong>CSV, XLS, XLSX</strong> (Tối đa 10MB)
                </p>
                <button className="btn btn-primary">
                  <i className="bi bi-folder2-open me-2"></i>
                  Chọn file từ máy tính
                </button>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx"
            multiple
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            disabled={uploading}
          />
        </div>
      </div>

      {/* Upload Mode Selection */}
      {selectedFiles.length > 0 && (
        <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: '#fff9e6' }}>
          <div className="card-body">
            <div className="d-flex align-items-start">
              <i className="bi bi-gear-fill fs-5 text-warning me-3 mt-1"></i>
              <div className="flex-grow-1">
                <h6 className="mb-2">
                  <i className="bi bi-sliders me-2"></i>
                  Chế độ Upload
                </h6>
                <div className="btn-group btn-group-sm w-100" role="group">
                  <input 
                    type="radio" 
                    className="btn-check" 
                    name="uploadMode" 
                    id="mode-replace" 
                    value="replace"
                    checked={uploadMode === 'replace'}
                    onChange={(e) => setUploadMode(e.target.value)}
                  />
                  <label className="btn btn-outline-danger" htmlFor="mode-replace">
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Đè toàn bộ dữ liệu cũ
                  </label>

                  <input 
                    type="radio" 
                    className="btn-check" 
                    name="uploadMode" 
                    id="mode-append" 
                    value="append"
                    checked={uploadMode === 'append'}
                    onChange={(e) => setUploadMode(e.target.value)}
                  />
                  <label className="btn btn-outline-primary" htmlFor="mode-append">
                    <i className="bi bi-plus-circle me-1"></i>
                    Thêm dữ liệu mới
                  </label>

                  <input 
                    type="radio" 
                    className="btn-check" 
                    name="uploadMode" 
                    id="mode-merge" 
                    value="merge"
                    checked={uploadMode === 'merge'}
                    onChange={(e) => setUploadMode(e.target.value)}
                  />
                  <label className="btn btn-outline-success" htmlFor="mode-merge">
                    <i className="bi bi-magic me-1"></i>
                    Tự động (Đề xuất)
                  </label>
                </div>
                <div className="mt-2">
                  <small className="text-muted">
                    {uploadMode === 'replace' && (
                      <><i className="bi bi-exclamation-triangle text-danger me-1"></i>
                      Xóa tất cả file cũ của nguồn này, chỉ giữ lại file mới upload</>
                    )}
                    {uploadMode === 'append' && (
                      <><i className="bi bi-info-circle text-primary me-1"></i>
                      Giữ nguyên tất cả file cũ, thêm file mới vào</>
                    )}
                    {uploadMode === 'merge' && (
                      <><i className="bi bi-check-circle text-success me-1"></i>
                      Đè file trùng tên, giữ file khác, thêm file mới</>
                    )}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-gradient-info text-white d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">
                <i className="bi bi-files me-2"></i>
                File đã chọn ({selectedFiles.length})
              </h5>
            </div>
            <div>
              <button
                className="btn btn-sm btn-light me-2"
                onClick={() => setSelectedFiles([])}
                disabled={uploading}
              >
                <i className="bi bi-x-circle me-1"></i>
                Xóa tất cả
              </button>
              <button
                className="btn btn-sm btn-success"
                onClick={handleUploadAll}
                disabled={uploading || !selectedSourceId}
              >
                <i className="bi bi-cloud-upload me-1"></i>
                Upload {selectedFiles.length} file
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th>Tên file</th>
                    <th className="text-center">Loại</th>
                    <th className="text-end">Kích thước</th>
                    <th className="text-center" style={{ width: '100px' }}>Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedFiles.map((file, index) => {
                    const fileExtension = file.name.split('.').pop().toUpperCase();
                    const isExcel = ['XLS', 'XLSX'].includes(fileExtension);
                    
                    return (
                      <tr key={index}>
                        <td className="text-center text-muted">{index + 1}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className={`bi bi-file-earmark-${isExcel ? 'excel' : 'text'} fs-4 me-2 text-${isExcel ? 'success' : 'info'}`}></i>
                            <div>
                              <strong>{file.name}</strong>
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          <span className={`badge bg-${isExcel ? 'success' : 'info'}`}>
                            {fileExtension}
                          </span>
                        </td>
                        <td className="text-end">{formatFileSize(file.size)}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveFile(index)}
                            disabled={uploading}
                            title="Xóa file"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {!selectedSourceId && (
              <div className="alert alert-warning m-3 mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Lưu ý:</strong> Vui lòng chọn nguồn dữ liệu trước khi upload
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data Repository - Kho dữ liệu */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-gradient-primary text-white d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-1">
              <i className="bi bi-database me-2"></i>
              Kho Dữ liệu Tổng hợp
            </h5>
            <small className="text-white-50">
              <i className="bi bi-info-circle me-1"></i>
              {uploadedDataSources.length} nguồn dữ liệu • Dùng cho module Tiêu chí
            </small>
          </div>
          {uploadedDataSources.length > 0 && (
            <span className="badge bg-white text-primary fw-bold">
              <i className="bi bi-bar-chart me-1"></i>
              {uploadedDataSources.reduce((sum, ds) => sum + (ds.rowCount || 0), 0).toLocaleString()} records
            </span>
          )}
        </div>
        <div className="card-body p-0">
          {uploadedDataSources.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-database-slash fs-1 text-muted mb-3" style={{ fontSize: '4rem' }}></i>
              <h5 className="text-muted">Kho dữ liệu đang trống</h5>
              <p className="text-muted mb-3">Upload file CSV hoặc Excel để xây dựng kho dữ liệu</p>
              <div className="alert alert-info d-inline-block text-start" style={{ maxWidth: '500px' }}>
                <strong><i className="bi bi-lightbulb me-1"></i> Gợi ý:</strong> 
                <ul className="mb-0 mt-2 small">
                  <li>Download file mẫu từ <a href="/templates/ews_data_template.xlsx" className="alert-link">templates/ews_data_template.xlsx</a></li>
                  <li>Hoặc xem hướng dẫn chi tiết tại <a href="/templates/README.md" target="_blank" rel="noopener noreferrer" className="alert-link">README.md</a></li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Tên file</th>
                    <th className="text-center">Loại</th>
                    <th>Nguồn dữ liệu</th>
                    <th className="text-center">Sheets/Tables</th>
                    <th className="text-end">Rows</th>
                    <th className="text-end">Columns</th>
                    <th className="text-end">Kích thước</th>
                    <th>Ngày upload</th>
                    <th className="text-center">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedDataSources.map((dataSource, index) => (
                    <tr key={dataSource.id}>
                      <td className="text-center text-muted">{index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <i className={`bi bi-file-earmark-${dataSource.type === 'CSV' ? 'text' : 'excel'} fs-4 me-2 text-${dataSource.type === 'CSV' ? 'info' : 'success'}`}></i>
                          <div>
                            <strong>{dataSource.name}</strong>
                            {dataSource.data?.sheets && dataSource.data.sheets.length > 0 && (
                              <div className="small text-muted">
                                {dataSource.data.sheets.map(s => s.name).join(', ')}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className={`badge bg-${dataSource.type === 'CSV' ? 'info' : 'success'}`}>
                          {dataSource.type}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <i className="bi bi-folder me-2 text-primary"></i>
                          <div>
                            <strong className="text-primary">{dataSource.sourceName || 'N/A'}</strong>
                            {dataSource.sourceCode && (
                              <div className="small text-muted">[{dataSource.sourceCode}]</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-secondary">
                          {dataSource.sheetCount || 1}
                        </span>
                      </td>
                      <td className="text-end">
                        <strong>{(dataSource.rowCount || 0).toLocaleString()}</strong>
                      </td>
                      <td className="text-end">{dataSource.columnCount || 0}</td>
                      <td className="text-end text-muted">{formatFileSize(dataSource.fileSize)}</td>
                      <td className="text-muted small">{formatDate(dataSource.uploadDate)}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => handleView(dataSource)}
                          title="Xem dữ liệu"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-success me-1"
                          onClick={() => handleDownload(dataSource)}
                          title="Tải xuống"
                        >
                          <i className="bi bi-download"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(dataSource.id)}
                          title="Xóa"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Preview/View Modal */}
      {selectedFile && previewData && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={(e) => {
            if (e.target.className.includes('modal')) {
              setSelectedFile(null);
              setPreviewData(null);
            }
          }}
        >
          <div className="modal-dialog modal-xl modal-fullscreen-lg-down">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-table me-2"></i>
                  {selectedFile.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewData(null);
                  }}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {previewData.sheets.map((sheet, sheetIndex) => (
                  <div key={sheetIndex} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        <i className="bi bi-file-earmark-spreadsheet me-2 text-primary"></i>
                        <strong>{sheet.name}</strong>
                      </h6>
                      <span className="badge bg-secondary">
                        {sheet.data.length} rows × {sheet.headers.length} columns
                      </span>
                    </div>
                    
                    <div className="table-responsive" style={{ maxHeight: '500px' }}>
                      <table className="table table-sm table-bordered table-hover">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th className="text-center bg-dark text-white" style={{ width: '50px' }}>#</th>
                            {sheet.headers.map((header, index) => (
                              <th key={index} className="bg-primary text-white">{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sheet.data.slice(0, 100).map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              <td className="text-center bg-light fw-bold text-muted">{rowIndex + 1}</td>
                              {sheet.headers.map((header, colIndex) => (
                                <td key={colIndex}>{row[header] !== null && row[header] !== undefined ? String(row[header]) : '-'}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {sheet.data.length > 100 && (
                      <div className="alert alert-info mt-3">
                        <i className="bi bi-info-circle me-2"></i>
                        Chỉ hiển thị 100 rows đầu tiên. Tổng cộng: <strong>{sheet.data.length} rows</strong>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setSelectedFile(null);
                    setPreviewData(null);
                  }}
                >
                  <i className="bi bi-x-circle me-2"></i>
                  Đóng
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => handleDownload(selectedFile)}
                >
                  <i className="bi bi-download me-2"></i>
                  Tải xuống
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataUpload;
